import { useState } from 'react';
import { useAlerts, useAlertCounts, useDismissAlert, useMarkAllAlertsRead, useGenerateAlerts } from '@/hooks/useAlerts';
import { AlertType } from '@/types';
import Button from '@/components/ui/Button';
import { TrendingDown, Calendar, PawPrint, Check, RefreshCw, Bell } from 'lucide-react';
import clsx from 'clsx';

export default function Alerts() {
  const [filterType, setFilterType] = useState<AlertType | ''>('');
  const { data: alerts, isLoading, refetch } = useAlerts({
    alert_type: filterType || undefined,
  });
  const { data: counts } = useAlertCounts();
  const dismissAlert = useDismissAlert();
  const markAllRead = useMarkAllAlertsRead();
  const generateAlerts = useGenerateAlerts();

  const alertTypeConfig = {
    low_stock: {
      icon: TrendingDown,
      label: 'Stock Bajo',
      color: 'text-yellow-600 bg-yellow-100',
      borderColor: 'border-yellow-300',
    },
    expiration: {
      icon: Calendar,
      label: 'Por Vencer',
      color: 'text-orange-600 bg-orange-100',
      borderColor: 'border-orange-300',
    },
    next_visit: {
      icon: PawPrint,
      label: 'Próxima Visita',
      color: 'text-blue-600 bg-blue-100',
      borderColor: 'border-blue-300',
    },
  };

  const filterOptions = [
    { value: '', label: 'Todas', count: counts?.total || 0 },
    { value: 'low_stock', label: 'Stock Bajo', count: counts?.low_stock || 0 },
    { value: 'expiration', label: 'Por Vencer', count: counts?.expiration || 0 },
    { value: 'next_visit', label: 'Próximas Visitas', count: counts?.next_visit || 0 },
  ];

  const handleDismiss = async (id: number) => {
    try {
      await dismissAlert.mutateAsync(id);
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead.mutateAsync(filterType || undefined);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleGenerateAlerts = async () => {
    try {
      const result = await generateAlerts.mutateAsync();
      alert(`Se generaron ${result.generated.total} nuevas alertas`);
      refetch();
    } catch (error) {
      console.error('Error generating alerts:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas</h1>
          <p className="text-gray-600">
            {counts?.unread || 0} alertas sin leer
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleGenerateAlerts}
            loading={generateAlerts.isPending}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Verificar Alertas
          </Button>
          {(counts?.unread || 0) > 0 && (
            <Button
              variant="secondary"
              onClick={handleMarkAllRead}
              loading={markAllRead.isPending}
            >
              <Check className="h-4 w-4 mr-2" />
              Marcar todas leídas
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilterType(option.value as AlertType | '')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filterType === option.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            )}
          >
            {option.label}
            {option.count > 0 && (
              <span className="ml-2 bg-gray-200 px-2 py-0.5 rounded-full text-xs">
                {option.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lista de alertas */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : alerts && alerts.length > 0 ? (
          alerts.map((alert) => {
            const config = alertTypeConfig[alert.alert_type];
            const Icon = config.icon;

            return (
              <div
                key={alert.id}
                className={clsx(
                  'card flex items-start gap-4 border-l-4',
                  config.borderColor,
                  !alert.is_read && 'ring-1 ring-primary-200'
                )}
              >
                <div className={clsx('p-3 rounded-lg', config.color)}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{alert.title}</h3>
                    {!alert.is_read && (
                      <span className="w-2 h-2 bg-primary-500 rounded-full" />
                    )}
                  </div>
                  {alert.message && (
                    <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className={clsx('px-2 py-0.5 rounded', config.color)}>
                      {config.label}
                    </span>
                    <span>
                      {new Date(alert.created_at).toLocaleDateString('es-AR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismiss(alert.id)}
                  loading={dismissAlert.isPending}
                >
                  Descartar
                </Button>
              </div>
            );
          })
        ) : (
          <div className="card text-center py-12">
            <Bell className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No hay alertas activas</p>
          </div>
        )}
      </div>
    </div>
  );
}
